import { useEffect, useState } from "react";
import Select from "react-select";
import { GraduationCap, ChartLine, Users, Hourglass, CalendarDays } from "lucide-react";
import PageHeader from "../../components/PageHeader";
import { Row, Col, Card, Form, Button, Spinner, Alert } from "react-bootstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { LMSBASEURL } from "../../config/apiUrls";
import { isMobile } from "react-device-detect";
import { Funnel } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const TeacherBatchStudentActivityDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasActivity, setHasActivity] = useState(false);
  const instituteId = sessionStorage.getItem("studentInstituteId");
  const teacherId = sessionStorage.getItem("id");

  const today = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 14);
  const formatInputDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  const [startDate, setStartDate] = useState(formatInputDate(sevenDaysAgo));
  const [endDate, setEndDate] = useState(formatInputDate(today));
  const parseDateString = (s) => {
    try {
      const [y, m, d] = (s || "").split("-").map(Number);
      if (!y || !m || !d) return new Date();
      return new Date(y, m - 1, d);
    } catch {
      return new Date();
    }
  };
  const [selectedStudyingYear, setSelectedStudyingYear] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date());
  const [batchOptions, setBatchOptions] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const token = process.env.REACT_APP_API_TOKEN;

  useEffect(() => {
    if (selectedBatch) {
      fetchData(startDate, endDate);
    }
  }, [selectedBatch]);

  useEffect(() => {
    const fetchBatchList = async () => {
      try {
        const res = await fetch(`${LMSBASEURL}/api/batch/getlist`, {
          method: "POST",
          headers: {
            Authorization: token,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ teacher_id: parseInt(teacherId) })
        });
        const json = await res.json();
        const list = Array.isArray(json?.data?.data) ? json.data.data : [];
        const opts = list.map((b) => ({ value: b.id, label: b.batch_name || b.name || `Batch ${b.id}` }));
        setBatchOptions(opts);
        // Auto-select the first batch if available
        if (opts.length > 0) {
          setSelectedBatch(opts[0]);
        }
      } catch (err) {
        console.error("Batch list error", err);
        setBatchOptions([]);
      }
    };
    fetchBatchList();
  }, [LMSBASEURL, token, teacherId]);

  const fetchData = async (from = startDate, to = endDate,) => {
    try {
      setLoading(true);
      setError("");

      const headers = new Headers();
      headers.append("Authorization", token);
      headers.append("Accept", "application/json");
      headers.append("Content-Type", "application/json");

      const body = JSON.stringify({
        institute_ids: [parseInt(instituteId)],
        batch_id: selectedBatch?.value,
        start_date: from,
        end_date: to,
      });

      const response = await fetch(`${LMSBASEURL}/api/student-activity`, {
        method: "POST",
        headers,
        body,
        redirect: "follow",
      });

      const json = await response.json();
      if (!json.status) throw new Error(json.message || "API error");

      setSummary(json.summary);
      setChartData(transformChartData(json.data));
      setHasActivity(checkIfActivityExists(json.data));
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const checkIfActivityExists = (data) => {
    if (!data || data.length === 0) return false;
    return data.some((day) => day.total_logged_in_students > 0);
  };

  const transformChartData = (apiData) => {
    if (!apiData || apiData.length === 0) return [];

    const uniqueKeys = new Set();
    apiData.forEach((day) => {
      day.data.forEach((entry) => {
        uniqueKeys.add(`${entry.degree_title} (${entry.studying_year})`);
      });
    });

    return apiData.map((day) => {
      const dayObj = { date: formatDate(day.attendance_date) };
      uniqueKeys.forEach((key) => (dayObj[key] = 0));

      day.data.forEach((entry) => {
        const key = `${entry.degree_title} (${entry.studying_year})`;
        dayObj[key] = entry.total_logged_in_students;
      });

      return dayObj;
    });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const handleFilter = () => {
    fetchData(startDate, endDate, selectedStudyingYear?.value,selectedYear.getFullYear());
  };

  const handleReset = () => {
    setSelectedStudyingYear(null)
    setSelectedYear(new Date())
    setSelectedBatch(null)
    const newToday = new Date();
    const newSevenDaysAgo = new Date();
    newSevenDaysAgo.setDate(newToday.getDate() - 6);

    const from = formatInputDate(newSevenDaysAgo);
    const to = formatInputDate(newToday);

    setStartDate(from);
    setEndDate(to);
    fetchData(from, to);
  };

  const summaryCards = [
    {
      label: "Total Unique Logins",
      value: summary?.total_unique_students || 0,
      icon: <GraduationCap />,
      color: "primary",
    },
    {
      label: "Avg. Daily Logins",
      value: summary?.average_daily_logins || 0,
      icon: <ChartLine />,
      color: "warning",
    },
    {
      label: "Total Logins Till Date",
      value: summary?.total_logins_till_date || 0,
      icon: <Users />,
      color: "primary",
    },
    {
      label: "Total Time Spent",
      value: summary?.total_time_spend || "0s",
      icon: <Hourglass />,
      color: "warning",
    },
    {
      label: "Avg. Time Per Day",
      value: summary?.avg_time_per_day || "0s",
      icon: <CalendarDays />,
      color: "primary",
    },
  ];

  const handleEndDate=(e)=>{
    setEndDate(e.target.value)
  }
  const handleStateDate=(e)=>{
    if (e.target.value) {
      setStartDate(e.target.value);
    }
  }

  const FilterOption=()=>{
    return(
      <Form>
        <Row className="g-3 align-items-end">
          <Col xs={12} sm={6} md={3}>
            <Form.Label>Start Date</Form.Label>
            <DatePicker
              selected={parseDateString(startDate)}
              onChange={(date) => setStartDate(formatInputDate(date))}
              maxDate={new Date()}
              className="form-control"
              wrapperClassName="w-100"
              dateFormat="yyyy-MM-dd"
              shouldCloseOnSelect={false}
              showMonthDropdown
              showYearDropdown
            />
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Form.Label>End Date</Form.Label>
            <DatePicker
              selected={parseDateString(endDate)}
              onChange={(date) => setEndDate(formatInputDate(date))}
              maxDate={new Date()}
              className="form-control"
              wrapperClassName="w-100"
              dateFormat="yyyy-MM-dd"
              shouldCloseOnSelect={false}
              showMonthDropdown
              showYearDropdown
            />
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Form.Label>Batch</Form.Label>
            <Select
              options={batchOptions}
              value={selectedBatch}
              onChange={setSelectedBatch}
              isClearable
              placeholder="Select batch"
            />
          </Col>
          <Col xs={12} md={3}>
            <div className="d-flex flex-column flex-sm-row justify-content-sm-end gap-2">
              <Button
                variant="primary"
                className="w-100 w-sm-auto"
                onClick={handleFilter}
              >
               {loading?  <div className="d-flex align-items-center gap-1">Applying<Spinner animation="border" variant="light" size="sm"/></div>:"Apply"}
              </Button>
              <Button
                variant="outline-secondary"
                className="w-100 w-sm-auto"
                onClick={handleReset}
              >
                Reset
              </Button>
            </div>
          </Col>
        </Row>
      </Form>
    )
  }

  return (
    <>
      <div className="row">
        <div className="col-12">
          <PageHeader
            title="Student Login Activity"
            breadcrumbs={[
              { label: "Dashboard", path: "/institute-dashboard" },
              { label: "Student Login Activity", active: true },
            ]}
          />
        </div>
      </div>

      <div className="my-2">
        {error && (
          <Alert variant="danger" className="my-3">
            {error}
          </Alert>
        )}
        <div>
          <div className="d-none d-lg-block">
            <div className="mb-4 bg-white rounded-4 border border-2 p-4">
              <FilterOption/>
            </div>
          </div>

          <div className="d-flex d-lg-none justify-content-end align-items-center mb-3">
            <button
              className="btn btn-primary btn-sm"
              type="button"
              data-bs-toggle="offcanvas"
              data-bs-target="#mobileFilterCanvas"
            >
              Filter Option <Funnel size={16} />
            </button>
          </div>
          <div
            className="offcanvas offcanvas-end"
            tabIndex="-1"
            id="mobileFilterCanvas"
          >
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Filters</h5>
              <button
                type="button"
                className="btn-close text-reset"
                data-bs-dismiss="offcanvas"
              ></button>
            </div>
            <div className="offcanvas-body">
              <FilterOption/>
            </div>
          </div>
        </div>

        {!isMobile && (
        <div className="d-flex flex-wrap justify-content-center gap-2 mb-4">
            {summaryCards.map((item, index) => (
            <div
                key={index}
                className="w-100"
                style={{
                    flex: "1 1 calc(18% - 12px)",
                    minWidth: "200px",
                    maxWidth: "240px",
                }}
            >
                <Card className="h-100 border-2 rounded-4">
                <Card.Body className="p-2 text-center d-flex flex-column align-items-center justify-content-center">
                    <div
                    style={{
                        fontSize: "2rem",
                        padding: "10px",
                        borderRadius: "12px",
                        backgroundColor:
                        item.color === "primary"
                            ? "rgba(0, 123, 255, 0.1)"
                            : "rgba(255, 193, 7, 0.1)",
                        color: item.color === "primary" ? "#007bff" : "#ffc107",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "10px",
                        width: "60px",
                        height: "60px",
                    }}
                    >
                    {item.icon}
                    </div>
                    <h6 className="text-muted mb-1" style={{ fontSize: "0.9rem" }}>
                    {item.label}
                    </h6>
                    <h4 className="fw-bold mb-0">{item.value}</h4>
                </Card.Body>
                </Card>
            </div>
            ))}
        </div>
        )}

        {isMobile && (
        <Row className="justify-content-center g-2 mb-4">
            {summaryCards.map((item, index) => (
            <Col xs={6} key={index}>
                <Card className="h-100 border-2 rounded-4 text-center p-2">
                <Card.Body className="p-2 d-flex flex-column align-items-center justify-content-center">
                    <div
                    style={{
                        fontSize: "2rem",
                        padding: "10px",
                        borderRadius: "12px",
                        backgroundColor:
                        item.color === "primary"
                            ? "rgba(0, 123, 255, 0.1)"
                            : "rgba(255, 193, 7, 0.1)",
                        color: item.color === "primary" ? "#007bff" : "#ffc107",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "10px",
                        width: "50px",
                        height: "50px",
                    }}
                    >
                    {item.icon}
                    </div>
                    <h6 className="text-muted mb-1" style={{ fontSize: "0.8rem" }}>
                    {item.label}
                    </h6>
                    <h5 className="fw-bold mb-0">{item.value}</h5>
                </Card.Body>
                </Card>
            </Col>
            ))}
        </Row>
        )}
        
        <Card className="border-2 rounded-4 p-2">
          <Card.Body>
            <div className={`d-flex justify-content-between align-items-center mb-3 w-100 gap-3 ${isMobile && 'flex-wrap'}`}>
              <div>
                <h5 className="fw-bold" style={{ color: "#007bff" }}>
                  <i className="bi bi-bar-chart-fill me-2"></i>
                  Daily Logins by Degree & Studying Year
                </h5>
                <span className="text-muted small">
                  {summary?.summary_text}
                </span>
              </div>
            </div>

            {loading ? (
              <div className="text-center my-5 d-flex align-items-center justify-content-center" style={{height:200}}>
                <Spinner animation="border" variant="primary" />
              </div>
            )
            :
            <>
              {!hasActivity ? (
                <div className="py-4">
                  <h6 className="fw-bold text-center mb-3 text-muted">
                    No login activity between {summary?.date_range?.from} and {summary?.date_range?.to}.
                  </h6>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={420}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ angle: -45, textAnchor: "end", fontSize: 12, marginBottom:40 }} interval={0} stroke="#666" />
                    {!isMobile && (
                      <YAxis
                        label={{
                          value: "Students Logged In",
                          angle: -90,
                          position: "insideLeft",
                        }}
                        tick={{ fontSize: 12 }}
                        stroke="#666"
                      />
                    )}
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "8px",
                        fontSize: "12px",
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: "12px", paddingTop:20 }} iconType="rect" />
                    {chartData.length > 0 &&
                      Object.keys(chartData[0])
                        .filter((key) => key !== "date")
                        .map((key, idx) => (
                          <Bar
                            key={idx}
                            dataKey={key}
                            barSize={10}
                            stackId="a"
                            fill={["#007bff", "#17a2b8", "#28a745", "#ffc107", "#fd7e14"][idx % 5]}
                          />
                        ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </>
          }
          </Card.Body>
        </Card>
      </div>
    </>
  );
};

export default TeacherBatchStudentActivityDashboard;
